import React from 'react';
import type { CategoryScore, ReadinessGrade } from '../types';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

interface ScoreBreakdownProps {
  categories: Record<string, CategoryScore>;
}

const GRADE_ICON: Record<ReadinessGrade, React.ReactNode> = {
  strong: <CheckCircle2 size={14} className="text-emerald-400" />,
  moderate: <AlertTriangle size={14} className="text-amber-400" />,
  weak: <XCircle size={14} className="text-red-400" />,
};

const GRADE_BAR: Record<ReadinessGrade, string> = {
  strong: 'bg-emerald-500',
  moderate: 'bg-amber-500',
  weak: 'bg-red-500',
};

function CategoryRow({ cat }: { cat: CategoryScore }): React.ReactElement {
  const [expanded, setExpanded] = React.useState(cat.issues.length > 0);

  return (
    <div className="border-b border-slate-800/50 last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 py-2.5 px-1 text-left hover:bg-slate-800/30 transition-colors rounded"
      >
        {expanded ? <ChevronDown size={12} className="text-slate-500 shrink-0" /> : <ChevronRight size={12} className="text-slate-500 shrink-0" />}
        {GRADE_ICON[cat.grade]}
        <span className="text-xs font-bold text-slate-300 flex-1 uppercase tracking-wider">{cat.label}</span>
        <span className="text-xs font-mono text-slate-500">{Math.round(cat.weight * 100)}%</span>
        <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full ${GRADE_BAR[cat.grade]} rounded-full transition-all`} style={{ width: `${cat.score}%` }} />
        </div>
        <span className={`text-xs font-black tabular-nums w-7 text-right ${cat.grade === 'strong' ? 'text-emerald-400' : cat.grade === 'moderate' ? 'text-amber-400' : 'text-red-400'}`}>
          {cat.score}
        </span>
      </button>

      {expanded && (cat.issues.length > 0 || cat.positives.length > 0) && (
        <div className="pl-8 pb-2.5 space-y-1">
          {cat.positives.map((p, i) => (
            <p key={`p-${i}`} className="text-[11px] text-emerald-400/80 flex items-start gap-1.5">
              <span className="shrink-0 mt-0.5">+</span> {p}
            </p>
          ))}
          {cat.issues.map((issue, i) => (
            <p key={`i-${i}`} className="text-[11px] text-red-400/80 flex items-start gap-1.5">
              <span className="shrink-0 mt-0.5">-</span> {issue}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function ScoreBreakdown({ categories }: ScoreBreakdownProps): React.ReactElement {
  const sorted = Object.values(categories).sort((a, b) => b.weight - a.weight);

  return (
    <div className="space-y-0">
      {sorted.map(cat => <CategoryRow key={cat.label} cat={cat} />)}
    </div>
  );
}
